import * as React from 'react';

import classNames from 'classnames';
import Mousetrap from 'mousetrap';

import {Button} from 'shells/button/button';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Modal} from 'shells/modal/modal';
import {RangeInput} from 'shells/range-input/range-input';
import {Switch} from 'shells/switch/switch';
import {TextField} from 'shells/textfield/textfield';
import {Body} from 'shells/typography/body';
import {LabelWithContent} from 'shells/typography/label-with-content';

import {useTriggerPagesCrawl} from '../../graphql/hooks';

type Props = {
    onClose: () => void;
    refetchContextDocuments: () => Promise<any>;
    limitExceededReason: string | null | undefined;
    hasExceededLimit: boolean;
};

export function AddWebsiteModal(props: Props) {
    const [url, setUrl] = React.useState<string>('');
    const [isCrawlingSubpages, setIsCrawlingSubpages] = React.useState<boolean>(true);
    const [subpageKeywords, setSubpageKeywords] = React.useState<string>('');
    const [numberOfSubpages, setNumberOfSubpages] = React.useState<number>(5);

    const {triggerPagesCrawl} = useTriggerPagesCrawl();

    const isStartDisabled = url.trim() === '' || props.hasExceededLimit;

    const handleStartCrawl = React.useCallback(() => {
        if (isStartDisabled) {
            return;
        }

        const processedKeywords = subpageKeywords
            .split(',')
            .map((keyword) => keyword.trim())
            .filter((keyword) => keyword !== '');

        triggerPagesCrawl({
            urls: [url],
            subpageKeywords: processedKeywords,
            subpageLimit: numberOfSubpages,
        }).then(() => {
            props.refetchContextDocuments();
        });

        props.onClose();
    }, [isStartDisabled, subpageKeywords, triggerPagesCrawl, url, numberOfSubpages, props]);

    React.useEffect(() => {
        const handler = () => {
            if (!isStartDisabled) {
                handleStartCrawl();

                return false;
            }
        };

        Mousetrap.bind('enter', handler);

        return () => {
            Mousetrap.unbind('enter');
        };
    }, [handleStartCrawl, isStartDisabled]);

    const containerClassName = classNames('flex flex-dir-col gap-16 mb-32', {
        disabled: props.hasExceededLimit,
        'mt-16': props.hasExceededLimit,
    });

    return (
        <Modal
            isOpen={true}
            onClose={props.onClose}
            headerText='Add website'
            fitHeight={true}
            maxWidth={600}
        >
            <>
                {props.limitExceededReason && (
                    <FlashBanner type='error' hasBorder={true}>
                        <Body size='small' color='dark'>
                            {props.limitExceededReason}
                        </Body>
                    </FlashBanner>
                )}
                <div className={containerClassName}>
                    <StackLegacy spacing={24} isHorizontal={true}>
                        <LabelWithContent
                            label={
                                <Body size='small' color='dark' isBold={true}>
                                    Crawl subpages
                                </Body>
                            }
                            spacing={4}
                            content={
                                <div>
                                    <Switch
                                        isOn={isCrawlingSubpages}
                                        onChange={() => setIsCrawlingSubpages(!isCrawlingSubpages)}
                                        isRounded={true}
                                        isSmall={true}
                                        isDisabled={props.hasExceededLimit}
                                    />
                                </div>
                            }
                        />
                        {isCrawlingSubpages && (
                            <LabelWithContent
                                label={
                                    <Body size='small' color='dark' isBold={true}>
                                        # of pages to add
                                    </Body>
                                }
                                content={
                                    <RangeInput
                                        value={numberOfSubpages}
                                        onChange={(value) => setNumberOfSubpages(value)}
                                        min={1}
                                        max={25}
                                        removePercentageSuffix={true}
                                        variant='small'
                                        disabled={props.hasExceededLimit}
                                    />
                                }
                            />
                        )}
                    </StackLegacy>
                    {isCrawlingSubpages && (
                        <LabelWithContent
                            label={<Body color='dark'>Keywords</Body>}
                            spacing={8}
                            sublabel={
                                'When you use keywords, they help the chatbot understand what content is most important, ensuring key pages are crawled first.'
                            }
                            content={
                                <TextField
                                    value={subpageKeywords}
                                    onChange={(value) => {
                                        setSubpageKeywords(value);
                                    }}
                                    placeholder='Example: contact, privacy, about'
                                    // @ts-expect-error
                                    disabled={props.hasExceededLimit}
                                />
                            }
                        />
                    )}
                    <LabelWithContent
                        label={<Body color='dark'>Website URL</Body>}
                        spacing={8}
                        content={
                            <div className='flex align-center'>
                                <div className='flex-grow'>
                                    <TextField
                                        value={url}
                                        onChange={(value) => {
                                            setUrl(value);
                                        }}
                                        placeholder='Enter URL'
                                        // @ts-expect-error
                                        disabled={props.hasExceededLimit}
                                        focusOnMount={true}
                                        onEnter={handleStartCrawl}
                                    />
                                </div>
                            </div>
                        }
                    />
                    <StackLegacy
                        spacing={8}
                        justifyContent='end'
                        isHorizontal={true}
                        alignCenter={true}
                    >
                        <Button
                            variant='text'
                            onClick={props.onClose}
                            disabled={props.hasExceededLimit}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant='primary'
                            size='big'
                            disabled={isStartDisabled}
                            onClick={handleStartCrawl}
                        >
                            Start crawl
                        </Button>
                    </StackLegacy>
                </div>
            </>
        </Modal>
    );
}
